/**
 * Session
 * -------
 * Single source of truth for client-side auth state.
 *
 * - Stores the JWT access token (currently in localStorage).
 * - Exposes getToken()/setUserSession()/removeUserSession() as the ONLY public API.
 * - Enforces Option A: if token is expired/invalid -> clear session (treat as logged out).
 *
 * No other module should read/write localStorage for auth.
 */

const Session = {

    getToken() {
        const token = localStorage.getItem('token');
        if (!token) return null;

        // Expiry check (Option A): if expired, clear session and force re-login
        try {
            const payloadBase64Url = token.split('.')[1];
            if (!payloadBase64Url) {
                this.removeUserSession();
                return null;
            }

            const payloadBase64 = payloadBase64Url.replace(/-/g, '+').replace(/_/g, '/');
            const json = decodeURIComponent(
                atob(payloadBase64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );

            const payload = JSON.parse(json);
            const exp = payload?.exp;

            // treat missing/invalid exp as expired
            if (!exp) {
                this.removeUserSession();
                return null;
            }

            const now = Math.floor(Date.now() / 1000);
            const skewSeconds = 30; // small clock-skew safety
            if (exp <= (now + skewSeconds)) {
                this.removeUserSession();
                return null;
            }

            return token;
        } catch {
            this.removeUserSession();
            return null;
        }
    },

    getRole() {
        const usersRole = localStorage.getItem('usersRole');
        if (usersRole) return usersRole;
        else return null;
    },

    getUserId() {
        const userId = localStorage.getItem('userId');
        if (userId) return userId;
        else return null;
    },

    getUserName() {
        const userName = localStorage.getItem('userName');
        if (userName) return userName;
        else return null;
    },

    setUserSession(userName, token, userId, usersRole) {
        localStorage.setItem('userName', userName);
        localStorage.setItem('token', token);
        localStorage.setItem('userId', userId);
        localStorage.setItem('usersRole', usersRole);
    },

    removeUserSession() {
        localStorage.removeItem('userName');
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        localStorage.removeItem('usersRole');
    }
}

export default Session;